import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';

const HeadToHead = ({ players, games, gameStats }) => {
  const [playerAId, setPlayerAId] = useState('');
  const [playerBId, setPlayerBId] = useState('');

  const result = useMemo(() => {
    if (!playerAId || !playerBId || playerAId === playerBId) return null;

    // Group stats by game
    const statsByGame = {};
    gameStats?.forEach((s) => {
      if (!statsByGame[s.game_id]) statsByGame[s.game_id] = [];
      statsByGame[s.game_id].push(s);
    });

    // Find games where both players played AND were on OPPOSING teams
    const h2hGames = [];
    games?.forEach((game) => {
      const stats = statsByGame[game.id] || [];
      const statA = stats.find((s) => s.player_id === playerAId);
      const statB = stats.find((s) => s.player_id === playerBId);
      if (!statA || !statB) return;
      if (statA.team === statB.team) return; // Same team - not head-to-head
      h2hGames.push({ game, statA, statB });
    });

    // Find games where they were on the SAME team
    const sameTeamGames = [];
    games?.forEach((game) => {
      const stats = statsByGame[game.id] || [];
      const statA = stats.find((s) => s.player_id === playerAId);
      const statB = stats.find((s) => s.player_id === playerBId);
      if (!statA || !statB) return;
      if (statA.team !== statB.team) return;
      sameTeamGames.push({ game, statA, statB });
    });

    let aWins = 0, bWins = 0;
    let aTotalPts = 0, bTotalPts = 0;
    let aTotalReb = 0, bTotalReb = 0;
    let aTotalAst = 0, bTotalAst = 0;

    h2hGames.forEach(({ game, statA, statB }) => {
      if (game.winning_team === statA.team) aWins++;
      if (game.winning_team === statB.team) bWins++;
      aTotalPts += statA.points || 0;
      bTotalPts += statB.points || 0;
      aTotalReb += statA.rebounds || 0;
      bTotalReb += statB.rebounds || 0;
      aTotalAst += statA.assists || 0;
      bTotalAst += statB.assists || 0;
    });

    const n = h2hGames.length;
    let sameTeamWins = 0;
    sameTeamGames.forEach(({ game, statA }) => {
      if (game.winning_team === statA.team) sameTeamWins++;
    });

    return {
      games: h2hGames,
      totalH2H: n,
      aWins,
      bWins,
      aAvg: n > 0 ? {
        points: (aTotalPts / n).toFixed(1),
        rebounds: (aTotalReb / n).toFixed(1),
        assists: (aTotalAst / n).toFixed(1),
      } : null,
      bAvg: n > 0 ? {
        points: (bTotalPts / n).toFixed(1),
        rebounds: (bTotalReb / n).toFixed(1),
        assists: (bTotalAst / n).toFixed(1),
      } : null,
      sameTeamGames: sameTeamGames.length,
      sameTeamWins,
    };
  }, [playerAId, playerBId, games, gameStats]);

  const playerA = players?.find((p) => p.id === playerAId);
  const playerB = players?.find((p) => p.id === playerBId);

  const formatDate = (dateStr) => {
    try {
      const d = dateStr?.includes('/') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr || '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Player selectors */}
      <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
        <h2 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>
          <Icon name="Swords" size={16} color="var(--color-primary)" className="inline mr-2" style={{ verticalAlign: '-2px' }} />
          Pick Two Players
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="font-caption text-xs font-medium mb-1 block" style={{ color: 'var(--color-muted-foreground)' }}>Player A</label>
            <select
              value={playerAId}
              onChange={(e) => setPlayerAId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border font-body text-sm"
              style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
            >
              <option value="">— Select —</option>
              {players?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-caption text-xs font-medium mb-1 block" style={{ color: 'var(--color-muted-foreground)' }}>Player B</label>
            <select
              value={playerBId}
              onChange={(e) => setPlayerBId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border font-body text-sm"
              style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
            >
              <option value="">— Select —</option>
              {players?.map((p) => (
                <option key={p.id} value={p.id} disabled={p.id === playerAId}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!playerAId || !playerBId ? (
        <div className="rounded-xl border p-8 text-center" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
          <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>
            Select two players to see their head-to-head record.
          </p>
        </div>
      ) : result?.totalH2H === 0 ? (
        <div className="rounded-xl border p-8 text-center" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <Icon name="AlertCircle" size={32} color="var(--color-muted-foreground)" />
          <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>
            {playerA?.name} and {playerB?.name} have not played against each other yet.
          </p>
          {result?.sameTeamGames > 0 && (
            <p className="font-caption text-xs mt-2" style={{ color: 'var(--color-muted-foreground)' }}>
              (They've been teammates in {result.sameTeamGames} game{result.sameTeamGames !== 1 ? 's' : ''})
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Matchup summary card */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
            <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>
                Matchup Record ({result.totalH2H} game{result.totalH2H !== 1 ? 's' : ''})
              </h3>
            </div>
            <div className="grid grid-cols-3 items-center text-center">
              <div className="py-4 px-2">
                <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>{playerA?.name}</p>
                <p className="font-data text-3xl font-bold mt-1" style={{ color: result.aWins > result.bWins ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                  {result.aWins}
                </p>
                <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Wins</p>
              </div>
              <div className="py-4 px-2" style={{ borderLeft: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)' }}>
                <p className="font-caption text-xs uppercase tracking-wider" style={{ color: 'var(--color-muted-foreground)' }}>VS</p>
                <p className="font-data text-lg font-bold mt-1" style={{ color: 'var(--color-foreground)' }}>
                  {result.totalH2H}
                </p>
                <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Games</p>
              </div>
              <div className="py-4 px-2">
                <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>{playerB?.name}</p>
                <p className="font-data text-3xl font-bold mt-1" style={{ color: result.bWins > result.aWins ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                  {result.bWins}
                </p>
                <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Wins</p>
              </div>
            </div>

            {/* Average stats comparison */}
            <div style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-muted)' }}>
              <div className="px-4 py-2">
                <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-muted-foreground)' }}>
                  Averages when they face off
                </p>
                {['points', 'rebounds', 'assists'].map((stat) => {
                  const a = parseFloat(result.aAvg[stat]);
                  const b = parseFloat(result.bAvg[stat]);
                  return (
                    <div key={stat} className="flex items-center gap-3 py-1">
                      <span className="font-data text-sm w-10 text-right font-semibold" style={{ color: a >= b ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                        {result.aAvg[stat]}
                      </span>
                      <span className="font-caption text-xs flex-1 text-center uppercase tracking-wider" style={{ color: 'var(--color-muted-foreground)' }}>
                        {stat.substring(0, 3)}
                      </span>
                      <span className="font-data text-sm w-10 font-semibold" style={{ color: b >= a ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                        {result.bAvg[stat]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Same-team note */}
          {result.sameTeamGames > 0 && (
            <div
              className="rounded-lg px-4 py-3 flex items-center gap-2"
              style={{ backgroundColor: 'rgba(232,93,4,0.08)', border: '1px solid rgba(232,93,4,0.2)' }}
            >
              <Icon name="Users" size={14} color="var(--color-primary)" />
              <p className="font-caption text-xs" style={{ color: 'var(--color-foreground)' }}>
                They've also been teammates in {result.sameTeamGames} game{result.sameTeamGames !== 1 ? 's' : ''}
                {' '}({result.sameTeamWins}W-{result.sameTeamGames - result.sameTeamWins}L together)
              </p>
            </div>
          )}

          {/* Game log */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
            <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>Game Log</h3>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {result.games.map(({ game, statA, statB }) => {
                const aWon = game.winning_team === statA.team;
                return (
                  <div key={game.id} className="px-4 py-2.5 flex flex-wrap items-center justify-between gap-2" style={{ borderTop: '1px solid var(--color-border)' }}>
                    <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{formatDate(game.date)}</span>
                    <div className="flex items-center gap-2 font-data text-xs">
                      <span className="font-semibold" style={{ color: aWon ? '#16a34a' : 'var(--color-foreground)' }}>
                        {statA.points}/{statA.rebounds}/{statA.assists}
                      </span>
                      <span style={{ color: 'var(--color-muted-foreground)' }}>vs</span>
                      <span className="font-semibold" style={{ color: !aWon ? '#16a34a' : 'var(--color-foreground)' }}>
                        {statB.points}/{statB.rebounds}/{statB.assists}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeadToHead;
